import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import { securePut, secureGet, secureDelete } from "../../config/api";
import { SaveCancelTop } from "../../components/PrimaryButton";
import Breadcrumbs from "../../components/Breadcrumbs";
import Input from "../../components/Input";
import SnackbarNotification from "../../components/SnackbarNotification";
import VehicleRow from "../../components/VehicleRow";
import DeletePopup from "../../components/DeletePopup";
import ModuleWrapper from "../../components/ModuleWrapper";

import plusIcon from "../../icons/plus.svg";

const buildInitialFormValues = (baseFields = [], initialValues = {}) => {
  const seed = {};
  baseFields.forEach((f) => {
    const name = f.name;
    if (name === 'cityId' && initialValues.city) {
      seed[name] = initialValues.city.id ?? "";
    } else if (name === 'vehicleBrandId' && initialValues.brand) {
      seed[name] = initialValues.brand.id ?? "";
    } else {
      seed[name] = initialValues[name] ?? "";
    }
  });
  if (initialValues.id) seed.id = initialValues.id;
  return seed;
};

const ClientForm = ({
  title,
  initialValues = {},
  baseFields = [],
  onSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleBrands, setVehicleBrands] = useState([]);
  const [isDeleteVehiclePopupVisible, setDeleteVehiclePopupVisible] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const initialState = useMemo(
    () => buildInitialFormValues(baseFields, initialValues),
    [baseFields, initialValues]
  );

  const [formValues, setFormValues] = useState(initialState);
  
  const [vehicleForm, setVehicleForm] = useState({
    type: '',
    vehicleBrandId: '',
    color: '',
    fuelType: '',
    immatriculationNumber: '',
    vinNumber: '',
    model: '',
    firstRegistrationDate: '',
  });
  const [vehicleModels, setVehicleModels] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const vehicleTypeOptions = [
    { value: "CAR", label: t(`filters.vehicles.CAR`) },
    { value: "MOTORCYCLE", label: t(`filters.vehicles.MOTORCYCLE`) },
    { value: "TRUCK", label: t(`filters.vehicles.TRUCK`) },
    { value: "VAN", label: t(`filters.vehicles.VAN`) },
    { value: "SUV", label: t(`filters.vehicles.SUV`) },
    { value: "OTHER", label: t(`filters.vehicles.OTHER`) },
  ];

  const fuelTypeOptions = [
    { value: "GASOLINE", label: t(`filters.vehicles.GASOLINE`) },
    { value: "DIESEL", label: t(`filters.vehicles.DIESEL`) },
    { value: "ELECTRIC", label: t(`filters.vehicles.ELECTRIC`) },
    { value: "HYBRID", label: t(`filters.vehicles.HYBRID`) },
    { value: "LPG", label: t(`filters.vehicles.LPG`) },
    { value: "CNG", label: t(`filters.vehicles.CNG`) },
    { value: "OTHER", label: t(`filters.vehicles.OTHER`) },
  ];

  const handleVehicleInputChange = (name, value) => {
    // Date input returns { display, date } - keep only the plain yyyy-MM-dd part
    if (name === 'firstRegistrationDate' && value && typeof value === 'object' && value.date) {
      setVehicleForm(prev => ({ ...prev, [name]: value.date.slice(0, 10) }));
      return;
    }
    setVehicleForm(prev => ({ ...prev, [name]: value }));
  };

  const resetVehicleForm = () => {
    setVehicleForm({
      type: '',
      vehicleBrandId: '',
      color: '',
      fuelType: '',
      immatriculationNumber: '',
      vinNumber: '',
      model: '',
      firstRegistrationDate: '',
    });
  };

  // Fetch vehicle brands
  useEffect(() => {
    const fetchVehicleBrands = async () => {
      try {
        const { data } = await secureGet("/vehicle-brands", showSnackbar);
        const brandOptions = data.map(brand => ({
          value: brand.id,
          label: brand.name,
        }));
        setVehicleBrands(brandOptions);
      } catch (error) {
        console.error("Error fetching vehicle brands:", error);
        setVehicleBrands([]);
      }
    };
    fetchVehicleBrands();
  }, []);

  // Fetch vehicle models (optionally filtered by brand)
  useEffect(() => {
    const fetchVehicleModels = async () => {
      try {
        const { data } = await secureGet("/vehicle-models", showSnackbar);
        const list = Array.isArray(data) ? data : (data?.content ?? data ?? []);
        setVehicleModels(list.map((m) => ({
          value: m.id,
          label: m.name,
          vehicleBrandId: m.vehicleBrandId ?? m.vehicleBrand?.id,
        })));
      } catch (error) {
        console.error("Error fetching vehicle models:", error);
        setVehicleModels([]);
      }
    };
    fetchVehicleModels();
  }, []);

  const fetchVehicles = async () => {
    if (!initialValues.id) {
      console.log("Client ID not available, skipping vehicle fetch");
      return;
    }
    
    try {
      const { data } = await secureGet(`/vehicles/client/${initialValues.id}`, showSnackbar);
      setVehicles(data);
      console.log("Vehicles fetched:", data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };
  
  useEffect(() => {
    if (initialValues.id) {
      fetchVehicles();
    }
  }, [initialValues.id]);

  const modelOptions = vehicleForm.vehicleBrandId
    ? vehicleModels.filter((m) => String(m.vehicleBrandId) === String(vehicleForm.vehicleBrandId))
    : vehicleModels;

  const handleAddClick = () => {
    if (!vehicleForm.type || !vehicleForm.vehicleBrandId || !vehicleForm.color || !vehicleForm.model ||
        !vehicleForm.fuelType || !vehicleForm.immatriculationNumber || !vehicleForm.vinNumber) {
      showSnackbar('Veuillez remplir tous les champs du véhicule', 'error');
      return;
    }
    const selectedBrand = vehicleBrands.find(b => b.value === vehicleForm.vehicleBrandId);
    const selectedModel = vehicleModels.find(m => m.value === vehicleForm.model);
    const newVehicle = {
      ...vehicleForm,
      brandMake: selectedBrand?.label || '',
      model: selectedModel?.label ?? vehicleForm.model,
      vehicleModelId: selectedModel?.value ?? vehicleForm.model,
      tempId: `temp-${Date.now()}-${Math.random()}`,
    };
    console.log("newVehicle", newVehicle);
    
    setVehicles(prev => [...prev, newVehicle]);
    console.log("vehicles", vehicles);
    resetVehicleForm();
  };

  const handleEditVehicleClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowModule(true);
  };

  const handleDeleteVehicleClick = (vehicle) => {
    setDeleteVehiclePopupVisible(true);
    setVehicleToDelete(vehicle);
  };

  const handleModuleClose = (action = 'cancel') => {
    if (action === 'submit') {
      fetchVehicles();
    }
    setShowModule(false);
    setSelectedVehicle(null);
  };

  const handleDeleteVehicleConfirm = async () => {
    if (!vehicleToDelete) return;
    
    try {
      // Only call API if vehicle has an id and is not a temporary vehicle
      if (vehicleToDelete.id && !vehicleToDelete.tempId) {
        await secureDelete(`/vehicles/${vehicleToDelete.id}`, showSnackbar);
        showSnackbar(t('confirmation.vehicleDelete'), 'success');
      }
      
      // Filter out the vehicle to delete
      setVehicles(prev => {
        if (vehicleToDelete.id && !vehicleToDelete.tempId) {
          // Delete by id for saved vehicles
          return prev.filter(v => !v.id || v.id !== vehicleToDelete.id);
        } else if (vehicleToDelete.tempId) {
          // Delete by tempId for new unsaved vehicles
          return prev.filter(v => !v.tempId || v.tempId !== vehicleToDelete.tempId);
        } else {
          // Fallback: match by immatriculationNumber and vinNumber as unique identifiers
          return prev.filter(v => 
            v.immatriculationNumber !== vehicleToDelete.immatriculationNumber ||
            v.vinNumber !== vehicleToDelete.vinNumber
          );
        }
      });
      
    } catch (error) {
      console.error("Error deleting vehicle:", error);
    } finally {
      setDeleteVehiclePopupVisible(false);
      setVehicleToDelete(null);
    }
  };

  useEffect(() => {
    setFormValues(initialState);
  }, [initialState]);


  const handleInputChange = (name, value) =>
    setFormValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = baseFields.filter(
      (f) => f.required && (formValues[f.name] === "" || formValues[f.name] == null)
    );
    if (missing.length) {
      showSnackbar(
        `Veuillez remplir les champs obligatoires : ${missing
          .map((f) => f.label)
          .join(", ")}`,
        "error"
      );
      return;
    }
    console.log("vehicles", vehicles);
    console.log("formValues", formValues);

      const vehiclesPayload = vehicles.map(vehicle => ({
        ...(vehicle.id && !vehicle.tempId && { id: vehicle.id }),
        color: vehicle.color,
        fuelType: vehicle.fuelType,
        immatriculationNumber: vehicle.immatriculationNumber,
        model: vehicle.model,
        type: vehicle.type,
        vehicleBrandId: Number(vehicle.vehicleBrandId) || Number(vehicle.brand?.id),
        vinNumber: vehicle.vinNumber,
        ...(vehicle.firstRegistrationDate != null && vehicle.firstRegistrationDate !== '' && { firstRegistrationDate: vehicle.firstRegistrationDate }),
        ...(vehicle.vehicleModelId != null && { vehicleModelId: Number(vehicle.vehicleModelId) }),
      }));

    const requestBody = {
      ...(initialValues.id && { id: initialValues.id }),
      ...formValues,
      vehicles: vehiclesPayload,
    };

    console.log("Request body:", requestBody);

    try {
      setSubmitting(true);
      await securePut("/clients/add", requestBody, showSnackbar);
      onSuccess();

      history.push({
        pathname: "/clients",
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
          showSnackbar: true,
          snackbarMessage: "Client added successfully",
          snackbarSeverity: "success",
        },
      });
    } catch (err) {
      console.error("Error adding client:", err);
      showSnackbar("Une erreur est survenue.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (location.pathname.includes("details") && formValues.id) {
      history.push({
        pathname: `/clients/details/${formValues.id}`,
        state: {
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 100,
        },
      });
      return;
    }

    history.push({
      pathname: "/clients",
      state: {
        filters: location.state?.filters || {},
        page: location.state?.page || 0,
        size: location.state?.size || 100,
      },
    });
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Drivnest - {t("titles.clients")}</title>
      </Helmet>

      <div className="flex flex-col space-y-2">
        <Breadcrumbs />
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-bold text-black">{title}</h2>
          <SaveCancelTop onCancel={handleCancel} onSave={handleSubmit} loading={submitting} />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {baseFields.length > 0 && (
          <div className="grid grid-cols-4 gap-4">
            {baseFields
              .filter((field) => {
                // If field has a showCondition function, evaluate it
                if (field.showCondition) {
                  return field.showCondition(formValues);
                }
                return true;
              })
              .map((field, idx) => (
              <Input
                key={idx}
                label={field.label}
                name={field.name}
                type={field.type || "text"}
                value={formValues[field.name] ?? ""}
                onChange={(v) => handleInputChange(field.name, v)}
                options={field.options}
                required={field.required}
              />
            ))}
          </div>
        )}
      </form>

      <div className="">
        <h3 className="text-[16px] font-semibold text-[#ACAFC6] mb-4"> {t(`fields_types.vehicleList`)}</h3>
        
        {/* Vehicle Input Form */}
        {/* make the inputs and the button share the full avaialble width  */}
        <div className="flex items-end gap-1.5 mb-4">
        <div className="grid grid-cols-8 gap-1.5">
          <Input
            type="dropdown"
            label={t(`filters.vehicles.type`)}
            name="type"
            value={vehicleForm.type ?? ""}
            onChange={(value) => handleVehicleInputChange('type', value)}
            options={vehicleTypeOptions}
            required
          />

          
          <Input
            type="dropdown"
            label={t(`filters.vehicles.brand`)}
            name="vehicleBrandId"
            value={vehicleForm.vehicleBrandId ?? ""}
            onChange={(value) => handleVehicleInputChange('vehicleBrandId', value)}
            options={vehicleBrands}
            required
          />
          
          <Input
            type="text"
            label={t(`filters.vehicles.color`)}
            name="color"
            value={vehicleForm.color ?? ""}
            onChange={(value) => handleVehicleInputChange('color', value)}
            required
          />
          
          <Input
            type="dropdown"
            label={t(`filters.vehicles.fuelType`)}
            name="fuelType"
            value={vehicleForm.fuelType ?? ""}
            onChange={(value) => handleVehicleInputChange('fuelType', value)}
            options={fuelTypeOptions}
            required
          />

          <Input
            type="dropdown"
            label={t(`filters.vehicles.model`)}
            name="model"
            value={vehicleForm.model ?? ""}
            onChange={(value) => handleVehicleInputChange('model', value)}
            options={modelOptions}
            required
          />
          <Input
            type="date"
            label={t(`filters.vehicles.firstRegistrationDate`) || "Date de mise en circulation"}
            name="firstRegistrationDate"
            value={vehicleForm.firstRegistrationDate ?? ""}
            onChange={(value) => handleVehicleInputChange('firstRegistrationDate', value)}
          />
          <Input
            type="text"
            label={t(`filters.vehicles.registrationNumber`)}
            name="immatriculationNumber"
            value={vehicleForm.immatriculationNumber ?? ""}
            onChange={(value) => handleVehicleInputChange('immatriculationNumber', value)}
            required
          />
          
          <Input
            type="text"
            label={t(`filters.vehicles.vin`)}
            name="vinNumber"
            value={vehicleForm.vinNumber ?? ""}
            onChange={(value) => handleVehicleInputChange('vinNumber', value)}
            required
          />

        </div>
          <button
            type="button"
            onClick={handleAddClick}
            className="bg-[#16283E] w-[48px] h-[48px] rounded-[12px] flex items-center justify-center flex-shrink-0"
          >
            <img src={plusIcon} alt="Add" className="w-6 h-6" />
          </button>
      </div>
      </div>

      <div className="space-y-2">
        {vehicles.map((v, index) => (
          <VehicleRow
            key={v.id || v.tempId || index}
            vehicle={v}
            onDelete={handleDeleteVehicleClick}
            onEdit={() => handleEditVehicleClick(v)}
          />
        ))}
      </div>

      {showModule && (
        <ModuleWrapper
          moduleType="vehicles"
          mode="modify"
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedVehicle}
        />
      )}

      {isDeleteVehiclePopupVisible && (
        <DeletePopup
          open={isDeleteVehiclePopupVisible}
          onCancel={() => setDeleteVehiclePopupVisible(false)}
          onConfirm={handleDeleteVehicleConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </div>
  );
};

export default ClientForm;
